import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLanguage } from 'src/common/hooks';
import { client, handleToastError } from 'src/utils/api-client';
import { GhgFactorTableEntrySchema } from './types';
import { GhgDataEntryCompletedStatisticsSchema } from './interfaces';
import { saveBlob } from 'src/utils/helpers';
import { ReportingFreq } from 'src/redux-file/interfaces';

// eslint-disable-next-line
export const useGhgSidebarPermissions = (
  organizationId: number,
  year: number
) => {
  const queryKey = ['ghgSidebarPermissions', { organizationId, year }];
  const query = useQuery(queryKey, () =>
    client
      .get<{ [key: string]: boolean }>(
        '/web/user-organization-permissions/ghg-sidebar',
        {
          params: { organization_id: organizationId, year },
        }
      )
      .then((res) => res.data)
  );

  const data = query.data || {};

  const hasAccess = (scope: string, section: string) =>
    data[`${scope}::${section}`] || false;

  return { data, hasAccess };
};

export const useGhgFactorEntries = () => {
  const { language } = useLanguage();
  const queryKey = ['ghgFactorEntries', language];

  const query = useQuery(queryKey, () =>
    client
      .get<GhgFactorTableEntrySchema[]>('/web/ghg-factors/factor-entries')
      .then((res) => res.data)
  );

  const data = query.data || [];

  return { ...query, data };
};

export const useGhgDataEntriesCompletedStatistics = (
  organizationId: number,
  fromPeriod: string,
  toPeriod: string
) => {
  const query = useQuery<GhgDataEntryCompletedStatisticsSchema[]>(
    ['data-entries-completed', { organizationId, fromPeriod, toPeriod }],
    () =>
      client
        .get('/web/v2/data-entries-completed/statistics', {
          params: {
            organization_id: organizationId,
            from_period: fromPeriod,
            to_period: toPeriod,
          },
        })
        .then((res) => res?.data)
  );
  return { ...query, data: query.data };
};

type UploadEmployeeEmissionsPayload = {
  organizationId: number;
  file: File;
  period?: string;
  withoutExtrapolation?: boolean;
};

type GhgMediaImportPayload = {
  organizationId: number;
  file: File;
  year: number;
  includeAuditWorkbook: boolean;
};

type GhgReportExportPayload = {
  organizationId: number;
  organizationIds: number[];
  startPeriod: string;
  endPeriod: string;
  reportingFreq: ReportingFreq;
};

export type GhgMediaImportSummary = {
  run_tag: string;
  prepared_fragments: number;
  aggregated_groups: number;
  imported_groups: number;
  omitted_groups: number;
  saved_item_counts: Record<string, number>;
};

export type GhgMediaImportHistoryItem = {
  id: number;
  run_tag: string;
  created_at: string;
  created_by_email: string | null;
  source_filename: string;
  target_year: number;
  imported_groups: number;
  omitted_groups: number;
};

export type GhgReportExportOrganization = {
  id: number;
  name: string;
  parent_id: number | null;
  reporting_freq: ReportingFreq;
};

export type GhgReportExportOptions = {
  reporting_freq: ReportingFreq;
  organizations: GhgReportExportOrganization[];
};

export const extractFilename = (
  contentDisposition?: string,
  defaultFilename = 'ghg_media_import_audit.csv'
) => {
  if (!contentDisposition) return defaultFilename;
  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
  const match = contentDisposition.match(/filename="?(.*?)"?(;|$)/i);
  return match?.[1] || defaultFilename;
};

export const useUploadEmployeeEmissions = () => {
  const { language } = useLanguage();
  const uploadXlsx = useMutation(
    ({
      organizationId,
      file,
      period,
      withoutExtrapolation = false,
    }: UploadEmployeeEmissionsPayload) => {
      const formData = new FormData();
      formData.append('file', file);

      return client.post('/web/ghg/employee-surveys/upload-xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: {
          organization_id: organizationId,
          without_extrapolation: withoutExtrapolation,
          ...(period ? { period } : {}),
        },
      });
    }
  );

  const downloadTemplate = async () => {
    try {
      const response = await client.get(
        '/web/ghg/employee-surveys/upload-xlsx-template',
        { responseType: 'blob', params: { language } }
      );

      // Create blob link and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `employee_survey_template_${language}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      throw error;
    }
  };

  return { uploadXlsx, downloadTemplate };
};

export const useGhgMediaImportHistory = (organizationId: number) => {
  const query = useQuery<GhgMediaImportHistoryItem[]>(
    ['ghgMediaImportHistory', organizationId],
    () =>
      client
        .get('/web/ghg/media-import/history', {
          params: { organization_id: organizationId },
        })
        .then((res) => res.data)
  );

  return { ...query, data: query.data || [] };
};

export const useDeleteGhgMediaImportRun = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      historyId,
    }: {
      organizationId: number;
      historyId: number;
    }) =>
      client.delete(`/web/ghg/media-import/history/${historyId}`, {
        params: { organization_id: organizationId },
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        'ghgMediaImportHistory',
        variables.organizationId,
      ]);
    },
    onError: handleToastError,
  });
};

export const useGhgMediaImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      file,
      year,
      includeAuditWorkbook,
    }: GhgMediaImportPayload) => {
      const formData = new FormData();
      formData.append('organization_id', String(organizationId));
      formData.append('year', String(year));
      formData.append(
        'include_audit_workbook',
        includeAuditWorkbook ? 'true' : 'false'
      );
      formData.append('file', file);

      if (includeAuditWorkbook) {
        const response = await client.post('/web/ghg/media-import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob',
        });

        return {
          type: 'audit' as const,
          blob: response.data,
          filename: extractFilename(response.headers['content-disposition']),
        };
      }

      const response = await client.post<GhgMediaImportSummary>(
        '/web/ghg/media-import',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return {
        type: 'summary' as const,
        summary: response.data,
      };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries([
        'ghgMediaImportHistory',
        variables.organizationId,
      ]);
      if (result.type === 'audit') {
        saveBlob(result.blob, result.filename);
      }
    },
    onError: handleToastError,
  });
};

export const useGhgReportExportOptions = (organizationId: number) => {
  const query = useQuery<GhgReportExportOptions>(
    ['ghgReportExportOptions', organizationId],
    () =>
      client
        .get('/web/ghg/report-export/options', {
          params: { organization_id: organizationId },
        })
        .then((res) => res.data),
    { enabled: !!organizationId }
  );

  return {
    ...query,
    data: query.data || null,
  };
};

export const useGhgReportExport = () =>
  useMutation({
    mutationFn: async ({
      organizationId,
      organizationIds,
      startPeriod,
      endPeriod,
      reportingFreq,
    }: GhgReportExportPayload) => {
      const response = await client.get('/web/ghg/report-export/download', {
        params: {
          organization_id: organizationId,
          organization_ids: organizationIds.join(','),
          start_period: startPeriod,
          end_period: endPeriod,
          reporting_freq: reportingFreq,
        },
        responseType: 'blob',
      });

      return {
        blob: response.data,
        filename: extractFilename(
          response.headers['content-disposition'],
          'ghg_report.xlsx'
        ),
      };
    },
    onSuccess: ({ blob, filename }) => {
      saveBlob(blob, filename);
    },
    onError: handleToastError,
  });
